// Edit-to-cut: pending cuts, undo, filler & pause cleanup, preview while
// playing, and saving (which renders the edited audio and re-masters it).

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cutAt, normalizeCuts, totalCutMs, type Cut } from '@podcastwiz-app/shared';
import { clock, sameCuts, useAiResults, useJob, useRunTool, useSaveCuts } from '../../lib/transcripts';
import { jobError } from '../../lib/jobs';
import { errorInfo } from '../../lib/api';
import type { MediaFile } from '../../lib/shows';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { usePlayer } from './Player';

export interface Edits {
  cuts: Cut[];
  dirty: boolean;
  canUndo: boolean;
  apply: (next: Cut[]) => void;
  undo: () => void;
  discard: () => void;
}

/** Pending cuts on top of the saved ones, with undo. */
export function useEdits(saved: Cut[]): Edits {
  const [pending, setPending] = useState<Cut[] | null>(null);
  const [history, setHistory] = useState<Cut[][]>([]);
  const cuts = pending ?? saved;
  return {
    cuts,
    dirty: pending !== null && !sameCuts(pending, saved),
    canUndo: history.length > 0,
    apply: (next) => {
      setHistory((h) => [...h, cuts]);
      setPending(normalizeCuts(next));
    },
    undo: () => {
      const prev = history[history.length - 1];
      setHistory((h) => h.slice(0, -1));
      setPending(prev ?? null);
    },
    discard: () => {
      setPending(null);
      setHistory([]);
    },
  };
}

/** While playing with preview on, jump over cut ranges. */
export function useSkipCuts(cuts: Cut[], enabled: boolean) {
  const player = usePlayer();
  const { currentMs, playing, seek } = player;
  useEffect(() => {
    if (!enabled || !playing) return;
    const c = cutAt(currentMs, cuts);
    if (c) seek(c.endMs);
  }, [currentMs, playing, enabled, cuts, seek]);
}

export interface FillerCut extends Cut {
  kind: 'filler' | 'pause' | 'repeat';
}

export function EditBar({
  media,
  transcriptionId,
  episodeId,
  edits,
  preview,
  onPreview,
}: {
  media: MediaFile;
  transcriptionId: string;
  episodeId: string;
  edits: Edits;
  preview: boolean;
  onPreview: (on: boolean) => void;
}) {
  const save = useSaveCuts(media.id);
  const results = useAiResults(transcriptionId);
  const run = useRunTool(transcriptionId, episodeId);
  const toast = useToast();
  const qc = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const job = useJob(jobId);

  const status = job.data?.status;
  useEffect(() => {
    if (status === 'completed') {
      void qc.invalidateQueries({ queryKey: ['media', media.id] });
      void qc.invalidateQueries({ queryKey: ['episode', episodeId] });
      toast.show('Edited audio is ready — podcast apps get the new version.', 'success');
      // Clearing the job ends the polling, so this runs once per render.
      setJobId(null);
    }
  }, [status, qc, media.id, episodeId, toast]);

  const fillers = results.data?.['filler-detect'];
  const fillerCuts = (fillers?.status === 'completed' ? (fillers.outputData?.cuts as FillerCut[] | undefined) : undefined) ?? [];
  const uncovered = fillerCuts.filter((c) => !edits.cuts.some((e) => c.startMs >= e.startMs && c.endMs <= e.endMs));
  const finding = fillers?.status === 'pending' || fillers?.status === 'active' || (run.isPending && !fillers);

  const duration = media.durationMs ?? 0;
  const cutMs = totalCutMs(edits.cuts);
  const rendering = !!jobId && status !== 'failed';

  const findFillers = () =>
    run.mutate(
      { type: 'filler-detect', route: 'filler-detect', label: 'Fillers', blurb: '' },
      { onError: (err) => toast.show(errorInfo(err).message, 'error') },
    );

  const saveCuts = () =>
    save.mutate(edits.cuts, {
      onSuccess: (r) => {
        setJobId(r.jobId);
        edits.discard();
      },
      onError: (err) => toast.show(errorInfo(err).message, 'error'),
    });

  return (
    <div className="space-y-2 rounded-lg border border-border bg-surface p-3 text-sm" aria-label="Edits" role="region">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">
          {edits.cuts.length === 0
            ? 'No cuts'
            : `${edits.cuts.length} ${edits.cuts.length === 1 ? 'cut' : 'cuts'} · ${(cutMs / 1000).toFixed(1)} s shorter`}
        </span>
        {edits.cuts.length > 0 && duration > 0 && (
          <span className="text-muted">
            {clock(duration)} → {clock(duration - cutMs)}
          </span>
        )}
        <label className="ml-auto flex items-center gap-1.5 text-xs text-muted">
          <input type="checkbox" checked={preview} onChange={(e) => onPreview(e.target.checked)} />
          Skip cuts while playing
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {!fillers || fillers.status === 'failed' ? (
          <Button size="sm" variant="secondary" onClick={findFillers} loading={finding}>
            Find fillers & pauses
          </Button>
        ) : finding ? (
          <Button size="sm" variant="secondary" loading>
            Finding fillers & pauses
          </Button>
        ) : uncovered.length > 0 ? (
          <Button size="sm" variant="secondary" onClick={() => edits.apply([...edits.cuts, ...uncovered])}>
            Remove {describeCleanup(uncovered)} (−{(totalCutMs(uncovered) / 1000).toFixed(1)} s)
          </Button>
        ) : (
          <span className="text-xs text-muted">{fillerCuts.length ? 'Fillers & pauses removed.' : 'No fillers or long pauses found.'}</span>
        )}
        {fillers?.status === 'failed' && (
          <span className="text-xs text-danger" title={fillers.errorMessage ?? undefined}>
            {jobError(fillers.errorMessage)}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {edits.canUndo && (
            <Button size="sm" variant="ghost" onClick={edits.undo}>
              Undo
            </Button>
          )}
          {edits.dirty && (
            <Button size="sm" variant="ghost" onClick={edits.discard}>
              Discard
            </Button>
          )}
          <Button size="sm" onClick={saveCuts} disabled={!edits.dirty || rendering} loading={save.isPending}>
            Save & render
          </Button>
        </span>
      </div>
      {rendering && (
        <p role="status" className="text-xs text-muted">
          Rendering the edited audio and re-mastering… {job.data?.progress ? `${job.data.progress}%` : ''}
        </p>
      )}
      {status === 'failed' && (
        <p className="text-xs text-danger" title={job.data?.errorMessage ?? undefined}>
          Rendering failed: {jobError(job.data?.errorMessage ?? null)} Your cuts are saved; try again from Activity.
        </p>
      )}
      {!rendering && !edits.dirty && media.editedPath && (
        <p className="text-xs text-success">
          ✓ Podcast apps get the edited version{media.editedDurationMs ? ` (${clock(media.editedDurationMs)})` : ''}.
        </p>
      )}
      <p className="text-xs text-muted">Select words and press Delete to cut them. Click a cut word to restore it.</p>
    </div>
  );
}

/** "3 fillers, 1 repeat, 2 long pauses" */
export function describeCleanup(cuts: FillerCut[]): string {
  const n = (kind: FillerCut['kind']) => cuts.filter((c) => c.kind === kind).length;
  const part = (count: number, one: string, many: string) => (count ? `${count} ${count === 1 ? one : many}` : '');
  return [part(n('filler'), 'filler', 'fillers'), part(n('repeat'), 'repeat', 'repeats'), part(n('pause'), 'long pause', 'long pauses')]
    .filter(Boolean)
    .join(', ');
}
